import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, message, Typography } from "@mimber/ui-web"
import {
  resetPasswordSchema,
  mapCognitoError,
  confirmPasswordReset,
} from "@mimber/auth"
import { PasswordStrengthMeter } from "@/components/blocks/PasswordStrengthMeter"
import type {
  ResetPasswordFormProps,
  ResetPasswordFormData,
} from "./ResetPasswordForm.types"

const { Paragraph } = Typography

export const ResetPasswordForm: React.FunctionComponent<
  ResetPasswordFormProps
> = ({ email, onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  const newPassword = form.watch("newPassword")

  const handleSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true)
    try {
      await confirmPasswordReset(email, data.code, data.newPassword)
      message.success("Password reset successfully!")
      onSuccess?.()
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
      onError?.(error as Error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Paragraph>
          Enter the code sent to <strong>{email}</strong> and your new password.
        </Paragraph>
      </div>

      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="code"
          type="text"
          label="Verification Code"
          placeholder="Enter 6-digit code"
          maxLength={6}
          required
        />
        <InputField
          name="newPassword"
          type="password"
          label="New Password"
          placeholder="Create a new password"
          required
        />
        <PasswordStrengthMeter password={newPassword || ""} />
        <InputField
          name="confirmPassword"
          type="password"
          label="Confirm Password"
          placeholder="Confirm your password"
          required
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full"
        >
          Reset Password
        </Button>
      </Form>
    </>
  )
}
